/** @jsx jsx */

import React, {
  useContext,
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  memo,
} from 'react';
import { createPortal } from 'react-dom';
import { css, jsx } from '@emotion/core';
import ie11VerticalFlexCenterFix from 'services/ie11VerticalFlexCenterFix';
import getStore from 'services/getStore';
import pubSub from 'services/pubSub';
import getBlockById from 'services/apiHelpers/getBlockById';
import { useSelector, useDispatch } from 'react-redux';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import updateReadOnlyText from 'services/updateReadOnlyText';
import { motion } from 'framer-motion';
import { InteractionContext } from 'components/Layouts/ContainerLogical';
import { killAllAudio } from 'services/killAllAudio';
import { isIOSSafari } from 'services/isIOSSafari';
import { updateGlobalFontStylesFromTheme } from 'styles/updateGlobalFontStylesFromTheme';
import { State } from 'store/store';
import headingsStyles from 'components/Layouts/headingsStyles';

const store = getStore();

let container;
let modalOpen = false;

interface IModal {
  useFader?: boolean;
  disableNullSpaceClick?: boolean;
  minWidth?: string;
  maxWidth?: string | number;
  onClose?: () => void;
  onOpened?: () => void;
  contentContainerStyles?: { [key: string]: string | number };
  children: any;
}

const Modal = memo(
  ({
    useFader = true,
    disableNullSpaceClick = false,
    minWidth = '50vw',
    maxWidth = 1024,
    onClose,
    onOpened = () => {},
    contentContainerStyles = {},
    children,
  }: IModal): any => {
    if (!container) {
      container = document.querySelector('#modal-container');
    }

    const interactionContext = useContext(InteractionContext);

    const [btnColor, setBtnColor] = useState<string | undefined>(undefined);

    const focusedNodeIdOnOpen = useSelector<any>(
      ({ focusedNodeId }) => focusedNodeId
    );

    const theme = useSelector((state: State) => {
      return state.theme || {};
    });

    const { fontSize = 16, lineHeight = 1.5, headings } = theme;
    const { h1, h2, h3, h4, h5, h6 } = headings || {};

    const rootContainer = useRef<HTMLDivElement>(null);
    const contentContainer = useRef<HTMLDivElement | null>(null);
    const hiddenButton = useRef(null);
    const buttonTabIndex = useRef(0);
    const checkResize = useRef(false);

    const getButtonAsAnchorOrButton = (
      container: HTMLElement
    ): HTMLAnchorElement | HTMLButtonElement | null => {
      if (!container) {
        return null;
      }

      return (
        container.querySelector('[class*="navigationButton"]') ||
        container.querySelector('[class*="genericButton"]') ||
        null
      );
    };

    useEffect(() => {
      modalOpen = true;
      window['MODAL_OPEN'] = true;

      if (!contentContainer.current) {
        return;
      }

      // the deal here is following the 'conventional' handling of modals
      // focus is captured within modal and all access to body content is disabled
      // only default focusable elements are added to a list of focusableElements
      // ( unless we explicitly add the contentContainer as the first element )
      // on first Tab the first focusbable element gains focus. then we cycle <>

      // we can also go with adding the contentContainer as the first element
      // judgement call ?

      //

      const focusableElements = [
        contentContainer.current,
        ...(Array.from(
          contentContainer.current.querySelectorAll(`a, button`)
        ) as HTMLElement[]),
      ];

      let firstFocusElement = focusableElements[0];
      let lastFocusElement = focusableElements[focusableElements.length - 1];
      //
      let tabInit;

      firstFocusElement.focus();

      const tabOveride = e => {
        if (!['Tab'].includes(e.key)) {
          return;
        }

        const reverseTab = e.shiftKey;

        // we only run this if we're NOT giving the contentContainer focus
        if (!tabInit) {
          // we do this so that we're not setting the focus of the contentContainer
          // on modal open. screen reader read content twice

          tabInit = true;
        }

        if (e.target === firstFocusElement && reverseTab) {
          e.preventDefault();
          lastFocusElement?.focus();
          //
          return;
        }

        if (e.target === lastFocusElement && !reverseTab) {
          e.preventDefault();
          firstFocusElement?.focus();
        }
      };

      window.addEventListener('keydown', tabOveride);

      const root = document.querySelector('#root')!;
      //
      if (window?.dev && !window.dev()) {
        root?.setAttribute('inert', '');
      }

      document.body.style.overflowY = 'hidden';

      const onResize = () => {
        if (!rootContainer.current) {
          return;
        }

        rootContainer.current.style.alignItems =
          window.innerHeight < contentContainer.current!.scrollHeight
            ? 'flex-start'
            : 'center';

        checkResize.current = true;
      };

      const tick = setInterval(() => {
        onResize();
        window.addEventListener('resize', onResize);
        if (checkResize.current) {
          clearInterval(tick);
        }
      }, 100);

      return () => {
        clearInterval(tick);

        modalOpen = false;
        window['MODAL_OPEN'] = false;

        root?.removeAttribute('inert');
        //
        document.body.style.overflowY = 'auto';

        checkResize.current = false;
        window.removeEventListener('resize', onResize);

        window.removeEventListener('keydown', tabOveride);
      };
    }, []);

    useEffect(() => {
      if (!window.dev) {
        // we're opening the modal from the editor so we don't need these checks
        return;
      }

      pubSub.subscribe('CLOSE_MODAL', $ => {
        onClose!();
      });

      return () => {
        const { focusedNodeId } = store.getState();

        if (!focusedNodeId) {
          return;
        }

        const block = getBlockById(focusedNodeId);

        const isNotInteraction = [
          'text',
          'image',
          'video',
          'svg',
          'embed',
          'editablebutton',
          'continuebutton',
        ].includes(block.type.toLowerCase());

        if (isNotInteraction) {
          if (focusedNodeIdOnOpen) {
            setCurrentBlock({ id: focusedNodeIdOnOpen });
          } else {
            setCurrentBlock(null);
          }
        }
      };
    }, []);

    useLayoutEffect(() => {
      if (window.dev && !window.dev()) {
        updateReadOnlyText(contentContainer.current);
      }
    }, []);

    useEffect(() => {
      if (window.dev && window.dev()) {
        return;
      }

      // find all the hrefs a

      const links = Array.from(contentContainer.current!.querySelectorAll('a'));

      if (!links.length) {
        return;
      }

      const closeOnLinkClick = e => {
        onClose!();
      };

      const addRemoveCloseOnLinkClick = addOrRemove => {
        links.forEach(link => {
          if (link.href.includes('#')) {
            link[`${addOrRemove}EventListener`]('click', closeOnLinkClick);
          }
        });
      };

      addRemoveCloseOnLinkClick('add');

      return () => {
        addRemoveCloseOnLinkClick('remove');
      };
    }, []);

    useEffect(() => {
      // ' proof of concept '

      if (interactionContext?.stopAudioWhenModalIsOpened) {
        killAllAudio();
      }

      //
    }, []);

    const globalFontStyles = updateGlobalFontStylesFromTheme({
      // fontSize,
      // lineHeight,
      theme,
      // parsedTextColor,
      headingsStyles() {
        return headingsStyles({ h1, h2, h3, h4, h5, h6 });
      },
    });

    return createPortal(
      // @ts-ignore
      <motion.div
        ref={rootContainer}
        initial={{ opacity: 0 }}
        transition={{
          duration: useFader ? 0.325 : 0,
          ease: 'easeOut',
        }}
        animate={{ opacity: 1 }}
        onAnimationComplete={onOpened}
        css={css`
          position: fixed;
          display: flex;
          justify-content: center;
          align-items: center;
          left: 0px;
          top: 0px;
          width: 100vw;
          min-height: 100vh;
          max-height: 100%;
          padding: ${isIOSSafari() ? '65px' : '10px'} 10px;
          background: rgba(0, 0, 0, 0.6);
          z-index: 1;
          ${ie11VerticalFlexCenterFix('height: 100vh;')}

          overflow-y: scroll;

          ${globalFontStyles}
        `}
      >
        <button
          ref={hiddenButton}
          css={css`
            position: absolute;
            left: 3px;
            top: 3px;
            width: calc(100% - 6px);
            height: calc(100% - 6px);

            border: none;
            outline: none;
            background: transparent;

            &:focus {
              border: 1px solid ${btnColor || '#282a2b'};
            }

            ${disableNullSpaceClick &&
              `
              pointer-events: none;
          `}
          `}
          onClick={(e: any) => {
            if (disableNullSpaceClick) {
              return;
            }
            //
            onClose!();
          }}
          aria-hidden="true"
        ></button>

        <div
          ref={contentContainer}
          css={css({
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            maxWidth,
            //: '1024px',
            borderRadius: '50px',
            background: '#fff',
            boxShadow: '0 0 12px 0 rgba(0, 0, 0, 0.2)',
            ...contentContainerStyles,

            'button:last-of-type': {
              position: 'relative',
            },
          })}
          onClick={e => e.stopPropagation()}
          tabIndex={0}
        >
          {children}
        </div>
      </motion.div>,
      container
    );
  },
  () => {
    // if it doesn't exist
    if (!window.dev) {
      return false;
    }

    return !window.dev();
  }
);

const useModal = ({ children }) => {
  const [, setState] = useState(true);

  return <Modal onClose={() => setState(false)}>{children}</Modal>;
};

export default Modal;

export { useModal };
